import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon/Icon";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import surHeaderHeight from "@tSurya/Handlers/SurHeaderHeightHandler";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    heading = "heading",
    backgroundColor = "backgroundColor",
    textColor = "textColor",
    enterLink = "enterLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.backgroundColor]: string;
        [fields.textColor]: string;
        [fields.enterLink]: LinkFieldValue;
    };
}

export interface BookAppointmentStyle {
    bookAppointment?: InjectableCss;
    textLinkStyles?: LinkPresentationProps;
    closeButtonStyles?: InjectableCss;
}

export const bookAppointmentStyle: BookAppointmentStyle = {
    bookAppointment: {
        css: css`
            display: flex;
            justify-content: center;
            gap: 10px;
            padding: 10px 0px;
            text-align: center;
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.baseBold};
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: ${TYPOGRAPHY.lineHeight.ssmall};
            text-transform: uppercase;
            max-width: 1280px;
            margin: 0 auto;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: calc(100% - 46px);
                            padding: 8px 0px;
                        `,
                        css`
                            max-width: calc(100% - 46px);
                            padding: 8px 0px;
                        `,
                        null,
                        css`
                            max-width: calc(100% - 80px);
                        `,
                        null,
                    ],
                    "max",
                );
            }}
            span {
                text-transform: uppercase;
            }
        `,
    },
    closeButtonStyles: {
        css: css`
            padding: 0px;
            background: white;
            line-height: 0;
            min-height: 16px;
            border: 0px;
            position: absolute;
            right: 1%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            top: 50%;
                            transform: translate(-50%, -50%);
                        `,
                        css`
                            top: 50%;
                            transform: translate(-50%, -50%);
                        `,
                        css`
                            top: 50%;
                            transform: translate(-50%, -50%);
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
            span {
                height: 16px;
                width: 16px;
            }
            &:hover {
                background: white;
            }
        `,
    },
};

const mapDispatchToProps = {
    surHeaderHeight,
};

const styles = bookAppointmentStyle;

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export const BookAppointmentOffers: FC<Props> = ({ fields, surHeaderHeight }) => {
    const [isClose, setIsClose] = useState(false);

    const closeHandler = () => {
        setIsClose(true);
    };

    useEffect(() => {
        const header: HTMLElement | null = document.querySelector("#MenuBAr");
        if (header) {
            const headerHeight = header.offsetHeight;
            surHeaderHeight({ headerHeight });
        }
    }, [isClose]);

    const enteredLink = useGetLink(fields.enterLink);

    const bookAmtContainer = {
        css: css`
            background-color: ${fields.backgroundColor};
            /* color: ${fields.textColor}; */
            position: relative;
        `,
    };

    const notActiveContainer = {
        css: css`
            display: none;
        `,
    };

    const textLinkStyles: LinkPresentationProps = {
        typographyProps: {
            css: css`
                color: ${fields.textColor} !important;
            `,
        },
    };

    return (
        <StyledWrapper {...(isClose === true ? { ...notActiveContainer } : { ...bookAmtContainer })}>
            <StyledWrapper {...styles.bookAppointment}>
                <Link href={enteredLink.url} {...textLinkStyles}>
                    {fields.heading}
                </Link>
                <Button
                    {...styles.closeButtonStyles}
                    onClick={closeHandler}
                    data-test-selector="Close Button"
                    aria-label="Close Button"
                >
                    <Icon src={X}></Icon>
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(BookAppointmentOffers),
    definition: {
        group: "Basic",
        displayName: "Book Appointment Offers",
        fieldDefinitions: [
            {
                name: fields.heading,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#000",
                fieldType: "General",
            },
            {
                name: fields.textColor,
                displayName: "Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#FFF",
                fieldType: "General",
            },
            {
                name: fields.enterLink,
                displayName: "Text Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
